import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { Course } from '../../../shared/models/interfaces/course.interface';
import { TrackService } from '../../../shared/services/track/track.service';
import { SelectModule } from 'primeng/select';
import { SpinnerSizeEnum } from '../../../shared/components/spinner/types/spinner-size.enum';
import { SpinnerColorEnum } from '../../../shared/components/spinner/types/spinner-color.enum';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { ButtonSizeEnum } from '../../../shared/components/button/types/button-size.enum';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { InputComponent } from '../../../shared/components/input/input.component';
import { SelectPillComponent, SelectPillItem } from '../../../shared/components/select-pill/select-pill.component';
import { CourseService } from '../../../shared/services/course/course.service';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { getCrudErrorMessage } from '../../../shared/utils/functions';
import { UploadService } from '../../../shared/services/upload/upload.service';

export interface CreateTrackModalResult {
  mode: 'create' | 'edit';
  trackId?: string;
  name: string;
  description: string;
  shortDescription: string;
  coursesIds: string[];
  coverImageUrl: string;
  imageFile?: File;
}

@Component({
  selector: 'l-create-track-modal',
  standalone: true,
  templateUrl: './create-track-modal.component.html',
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    IconComponent, 
    SelectPillComponent, 
    SelectModule,
    ButtonComponent,
    SpinnerComponent,
    InputComponent
  ],
})
export class CreateTrackModalComponent implements OnInit {
  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly config: DynamicDialogConfig,
    private readonly formBuilder: FormBuilder,
    private readonly courseService: CourseService,
    private readonly trackService: TrackService,
    private readonly toastService: ToastService,
    private readonly uploadService: UploadService,
  ) {
    this.formGroup = this.formBuilder.group({
      name: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      description: new FormControl<string>('', { nonNullable: true }),
      shortDescription: new FormControl<string>('', { nonNullable: true }),
      selectedCourseId: new FormControl<string>('', { nonNullable: true }),
      coursesIds: new FormControl<string[]>([], { nonNullable: true }),
      coverImageUrl: new FormControl<string>('', { nonNullable: true })
    });
  }

  public isUpdate: boolean = false;

  public trackId: string = '';

  public step: number = 1;

  public longDescriptionMax = 250;

  public formGroup!: FormGroup;

  public imageFile: File | null = null;

  public imageFileName: string = '';

  public imagePreviewUrl: string = '';

  public isSubmitting: boolean = false;

  public submitErrorMessage: string = '';

  public courses: Course[] = [];

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum

  public readonly buttonSizeEnum: typeof ButtonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public readonly spinnerColorEnum: typeof SpinnerColorEnum = SpinnerColorEnum;

  public readonly spinnerSizeEnum: typeof SpinnerSizeEnum = SpinnerSizeEnum;

  public get isDisabled(): boolean {
    return this.formGroup.invalid || this.isSubmitting;
  }

  public ngOnInit(): void {
    void this.loadCourses();

    if (this.config.data) {
      const modalData = this.config.data;

      this.isUpdate = modalData.isUpdate ?? modalData.mode === 'edit';
      this.trackId = modalData.trackId ?? modalData.id ?? '';

      if (!this.isUpdate) return;

      const track = modalData.track;

      this.formGroup.patchValue({
        name: modalData.name ?? track?.name ?? '',
        description: modalData.description ?? track?.description ?? '',
        shortDescription: modalData.shortDescription ?? track?.shortDescription ?? '',
        coursesIds: modalData.coursesIds ?? track?.coursesIds ?? [],
        coverImageUrl: modalData.coverImageUrl ?? track?.coverImageUrl ?? '',
      });

    }
  }

  public onRemoveCourse(course: string): void {
    const current = this.coursesIdsControl?.value ?? [];
    this.formGroup.patchValue({
      coursesIds: current.filter((c: string) => c !== course),
    });
  }

  public onAddCourse(course: string): void {
    const current = this.coursesIdsControl?.value ?? [];
    if (!current.includes(course)) {
      this.formGroup.patchValue({
        coursesIds: [...current, course],
      });
    }
  }

  public onCourseSelected(courseId: string | null | undefined): void {
    if (!courseId) {
      return;
    }

    this.onAddCourse(courseId);
    this.formGroup.patchValue({ selectedCourseId: '' });
  }

  public onClearCourses(): void {
    this.formGroup.patchValue({
      selectedCourseId: '',
      coursesIds: [],
    });
  }

  public onImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    this.clearLocalImagePreview();

    this.imageFile = file;
    this.imageFileName = file.name;
    this.imagePreviewUrl = URL.createObjectURL(file);
    this.formGroup.patchValue({
      coverImageUrl: '',
    });
  }

  public onImageUrlChange(value: string): void {
    this.formGroup.patchValue({ coverImageUrl: value });

    if (value.trim()) {
      this.clearLocalImagePreview();
    }
  }

  public goToStep(step: number): void {
    this.step = step;
  }

  public async onNext(): Promise<void> {
    if (this.step === 1) {
      this.goToStep(2);
      return;
    }

    if (!this.hasImagePreview) {
      return;
    }

    this.submitErrorMessage = '';
    this.isSubmitting = true;

    try {
      if (this.imageFile) {
        const publicUrl = await this.uploadService.uploadPublicImage(this.imageFile);
        this.formGroup.patchValue({ coverImageUrl: publicUrl });
        this.imageFile = null;
      }

      const formValue = this.formGroup.getRawValue();

      if (this.isUpdate) {
        if (!this.trackId) {
          throw new Error('Nao foi possivel identificar a trilha para editar.');
        }

        await this.trackService.update(this.trackId, {
          name: formValue.name ?? '',
          description: formValue.description ?? '',
          shortDescription: formValue.shortDescription ?? '',
          coursesIds: formValue.coursesIds ?? [],
          coverImageUrl: formValue.coverImageUrl ?? '',
        });
      } else {
        await this.trackService.create({
          name: formValue.name ?? '',
          description: formValue.description ?? '',
          shortDescription: formValue.shortDescription ?? '',
          coursesIds: formValue.coursesIds ?? [],
          coverImageUrl: formValue.coverImageUrl ?? '',
        });
      }

      const result: CreateTrackModalResult = {
        mode: this.isUpdate ? 'edit' : 'create',
        trackId: this.trackId || undefined,
        name: formValue.name ?? '',
        description: formValue.description ?? '',
        shortDescription: formValue.shortDescription ?? '',
        coursesIds: formValue.coursesIds ?? [],
        coverImageUrl: formValue.coverImageUrl ?? '',
      };

      this.ref.close(result);
    } catch (error) {
      this.submitErrorMessage = getCrudErrorMessage(error);
      this.toastService.error(this.submitErrorMessage, 'Oops...');
    } finally {
      this.isSubmitting = false;
    }
  }

  public onBack(): void {
    if (this.step === 2) {
      this.goToStep(1);
    } else {
      this.close();
    }
  }

  public close(): void {
    this.clearLocalImagePreview();
    this.ref.close();
  }

  public get isImageValid(): boolean {
    const url = this.coverImageUrlControl?.value ?? '';
    return !!url && url.startsWith('http');
  }

  public get hasImagePreview(): boolean {
    return !!this.imagePreviewUrl || this.isImageValid;
  }

  public get previewImageSrc(): string {
    const imageUrl = this.coverImageUrlControl?.value ?? '';
    return this.imagePreviewUrl || imageUrl;
  }

  public get modalTitle(): string {
    return this.isUpdate ? 'Editar trilha' : 'Criar nova trilha';
  }

  public get submitLabel(): string {
    return this.isUpdate ? 'Salvar' : 'Avançar';
  }

  public get longDescriptionCount(): number {
    const desc = this.descriptionControl?.value ?? '';
    return desc.length;
  }

  public get nameControl(): AbstractControl<string, string> | null {
    return this.formGroup.get('name');
  }

  public get descriptionControl(): AbstractControl<string, string> | null {
    return this.formGroup.get('description');
  }

  public get coursesIdsControl(): AbstractControl<string[], string[]> | null {
    return this.formGroup.get('coursesIds');
  }

  public get coverImageUrlControl(): AbstractControl<string, string> | null {
    return this.formGroup.get('coverImageUrl');
  }

  public get coursePillItems(): SelectPillItem[] {
    const selected = this.coursesIdsControl?.value ?? [];
    return selected.map((courseId) => ({
      value: courseId,
      label: this.getCourseTitle(courseId),
    }));
  }

  public getCourseTitle(courseId: string): string {
    return this.courses.find(course => course.id === courseId)?.title ?? courseId;
  }

  public isCourseSelected(courseId: string): boolean {
    return (this.coursesIdsControl?.value ?? []).includes(courseId);
  }

  private async loadCourses(): Promise<void> {
    this.courses = await this.courseService.getMany();
  }

  private clearLocalImagePreview(): void {
    if (this.imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }

    this.imagePreviewUrl = '';
    this.imageFile = null;
    this.imageFileName = '';
  }
}
